/* eslint-disable jsx-a11y/no-noninteractive-tabindex */
import {
  FocusEvent, FormEvent, KeyboardEvent, useEffect, useRef, useState,
} from 'react';

import SelectArrowIcon from 'assets/icons/selectArrow.svg';
import CloseIcon from 'assets/icons/close.svg';
import { FieldError, FieldErrors, FieldValues } from 'react-hook-form';
import { FormGroupModifier } from 'models/form';
import Spinner, { SpinnerModifier } from 'components/spinner';
import useModifiers from 'hooks/useModifiers';

// export type FormSelectOption = Record<string, unknown>;
export interface FormSelectOption extends Record<string, unknown> {
  isDisabled: boolean;
  value: string | number;
  label: string;
}

export interface FormSelectGroup {
  label?: string;
  options: FormSelectOption[];
}

export enum SelectModifier {
  NARROW = 'narrow',
  OVERVIEW = 'overview',
  DROPDOWN = 'dropdown',
}

export interface FormSelectProps {
  filterByKey: string;
  labelKey: string;
  valueKey: string;
  value?: string | number | null;
  groups: FormSelectGroup[];
  placeholder?: string;
  clearable?: boolean; // show x button
  searchable?: boolean; // allow search
  hideSelected?: boolean; // hide selected from options
  onChange: (v: string) => void;
  errors?: FieldErrors<FieldValues>;
  disabled?: boolean;
  name: string;
  label?: string;
  modifiers?: SelectModifier[];
  formOptionsEmbedded: boolean;
}

function FormSelect(props: FormSelectProps) {
  const mods = useModifiers(props.modifiers);
  const [currentOptions, setCurrentOptions] = useState(props.groups);
  const [selectedOption, setSelectedOption] = useState<FormSelectOption | undefined | null>(null);
  const [inputValue, setInputValue] = useState('');
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const error = props.errors && props.errors[props.name] as FieldError;
  const hasError = Boolean(error);
  const hasErrorMod = hasError ? FormGroupModifier.HAS_ERROR : '';
  const disabledMod = props.disabled ? FormGroupModifier.DISABLED : '';
  const activeMod = isMenuOpen ? FormGroupModifier.ACTIVE : '';

  // used to filter dropdown results based on current search
  const getFilteredOptions = (search: string): FormSelectGroup[] => props
    .groups.reduce((acc, curr) => {
      const filteredItems = (curr.options || []).filter((item) => (
        (item && item[props.filterByKey]) as string || ''
      ).toLowerCase().includes(search.toLowerCase()));

      return [...acc, { label: curr.label, options: filteredItems }];
    }, [] as FormSelectGroup[]);

  // used to filter dropdown results if hideSelected is set
  const getRemainingOptions = (search: string): FormSelectGroup[] => {
    if (!props.hideSelected) return props.groups;

    return props
      .groups.reduce((acc, curr) => {
        const filteredItems = (curr.options || [])
          .filter((item) => (
            (item && item[props.filterByKey]) as string || '').toLowerCase() !== search.toLowerCase());

        return [...acc, { label: curr.label, options: filteredItems }];
      }, [] as FormSelectGroup[]);
  };

  const setStates = (
    inputState: string,
    menuState: boolean,
    optionsState: FormSelectGroup[],
    selectedState: FormSelectOption | undefined | null,
  ) => {
    setInputValue(() => inputState);
    setIsMenuOpen(() => menuState);
    setCurrentOptions(() => optionsState);
    setSelectedOption(() => selectedState);
  };

  const resetStates = () => {
    setInputValue('');
    setIsMenuOpen(false);
    setCurrentOptions(props.groups);
    setSelectedOption(undefined);
  };

  const setDefaultStates = (value: string | number) => {
    // find default option based on value and set intial states
    const defaultGroup = props
      .groups.find((g) => g.options.find((o) => o[props.valueKey] === value));

    const defaultOption = defaultGroup?.options.find((o) => o[props.valueKey] === value);

    if (defaultOption) {
      const input = defaultOption[props.labelKey] as string;
      const filteredOptions = getRemainingOptions(defaultOption[props.filterByKey] as string);

      setStates(input, isMenuOpen, filteredOptions, defaultOption);
    } else {
      // setting selected option to null for default states to
      // not trigger form validation through change
      setStates('', isMenuOpen, props.groups, null);
    }
  };

  useEffect(() => {
    // update options list if props update through async load
    if (props.groups && props.groups.length) {
      setCurrentOptions(props.groups);

      // setting default states
      if (props.value) setDefaultStates(props.value);
    }
  }, [props.groups]);

  useEffect(() => {
    // need to destinguish between null and undefined in else loop for setting default states
    if (selectedOption) {
      props.onChange(selectedOption[props.valueKey] as string);
    } else if (selectedOption !== null) {
      props.onChange('');
    }
  }, [selectedOption]);

  const handleInputChange = (e: FormEvent<HTMLInputElement>) => {
    const currentInput = e.currentTarget.value;
    setInputValue(currentInput);
    setCurrentOptions(getFilteredOptions(currentInput));

    if (currentInput.length === 0) {
      setIsMenuOpen(true);
    }
  };

  const handleOptionClick = (option: FormSelectOption) => {
    const input = option[props.labelKey] as string;
    const filteredOptions = getRemainingOptions(option[props.filterByKey] as string);

    setStates(input, false, filteredOptions, option);
  };

  const handleOptionKeydown = (option: FormSelectOption, e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Enter') return;

    const input = option[props.labelKey] as string;
    const filteredOptions = getRemainingOptions(option[props.filterByKey] as string);

    if (!option.isDisabled) {
      setStates(input, false, filteredOptions, option);
    } else {
      resetStates();
    }
  };

  const handleClearClick = () => {
    resetStates();
  };

  const handleArrowClick = () => {
    if (props.disabled) return;
    setIsMenuOpen(!isMenuOpen);
  };

  const handleKeypress = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();

    const currentGroups = currentOptions.filter((optionGroup) => optionGroup.options.length);

    // select on enter, if only one option is left in the dropdown
    if (currentGroups.length === 1 && currentGroups[0].options.length === 1) {
      const key = currentOptions[0].options[0][props.filterByKey] as string;
      const filteredOptions = getRemainingOptions(key);
      const input = currentOptions[0].options[0][props.labelKey] as string;
      const currentSelectedOption = currentOptions[0].options[0];

      if (!currentSelectedOption.isDisabled) {
        setStates(input, false, filteredOptions, currentSelectedOption);
      } else {
        resetStates();
      }
    }
  };

  const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
    const selectEl = wrapperRef.current;
    const clickedInside = selectEl && selectEl.contains(e.relatedTarget);

    if (clickedInside || props.disabled) return;

    if (selectedOption) {
      if (inputValue === selectedOption[props.labelKey]) {
        setIsMenuOpen(false);
      } else if (inputValue !== selectedOption[props.labelKey]) {
        setInputValue(selectedOption[props.labelKey] as string);
        setIsMenuOpen(false);
      }
    } else {
      resetStates();
    }
  };

  const handleFocus = () => {
    if (props.disabled) return;
    setIsMenuOpen(true);
  };

  return (

    <div className={`ct_form-group ${hasErrorMod} ${disabledMod} ${activeMod}`}>
      {!!props.label && (
      <div className="ct_form-desc">
        <div className="ct_form-label">{props.label}</div>
      </div>
      )}
      {!!currentOptions && !!currentOptions.length && (
      <div className={`ct_select ${mods}`} tabIndex={0} onBlur={handleBlur} ref={wrapperRef}>
        <div className="ct_form-control">
          <input
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeypress}
            onFocus={handleFocus}
            className={`ct_textfield ${mods} ${props.clearable && inputValue ? '_hasButton' : ''}`}
            type="text"
            placeholder={props.placeholder}
            ref={inputRef}
            readOnly={!props.searchable}
            disabled={props.disabled}
          />
          {!!props.clearable && inputValue && (
          <button type="button" onClick={handleClearClick} className="ct_select-btn _clear">
            <CloseIcon />
          </button>
          )}
          <button type="button" onClick={handleArrowClick} className={`ct_select-btn _arrow ${isMenuOpen ? '_active' : ''}`}>
            <SelectArrowIcon />
          </button>
        </div>

        {currentOptions.some((group: FormSelectGroup) => group.options.length) && isMenuOpen && (
        <div className={`ct_select-options ${props.formOptionsEmbedded ? '_embedded' : ''} ${isMenuOpen ? '_open' : ''}`}>
          {currentOptions.map((group: FormSelectGroup, i: number) => (
            <div key={i} className={`ct_select-group ${group.options && group.options.length ? '' : '_hidden'}`}>
              {group.label && (<div className="ct_select-groupLabel">{group.label}</div>)}
              <div className="ct_select-groupItems">
                {group.options.map((option: FormSelectOption, index: number) => {
                  const selected = selectedOption && option[props.valueKey] === selectedOption[props.valueKey] ? '_selected' : '';
                  const disabled = option.isDisabled ? '_disabled' : '';

                  return (
                    <div
                      aria-hidden
                      key={index}
                      tabIndex={0}
                      className={`ct_select-option ${selected} ${disabled}`}
                      onClick={() => handleOptionClick(option)}
                      onKeyDown={(e) => handleOptionKeydown(option, e)}
                    >
                      {option[props.labelKey] as string}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
        )}

        {!currentOptions.some((group: FormSelectGroup) => group.options.length) && isMenuOpen && (
        <div className={`ct_select-options ${isMenuOpen ? '_open' : ''}`}>
          <div className="ct_select-groupItems">
            <div className="ct_select-noResults">No results</div>
          </div>
        </div>
        )}
      </div>
      )}

      {!!currentOptions && !currentOptions.length && (
        <div className={`ct_select _isLoading ${mods}`}>
          <div className="ct_form-control">
            <div className="ct_select-loading">
              <Spinner
                dimensions={
                  props.modifiers && props.modifiers.includes(SelectModifier.NARROW) ? 24 : 32
                }
                modifiers={[SpinnerModifier.SELECT]}
              />
            </div>
          </div>
        </div>
      )}

      {hasError && error && (
        <div className="ct_form-msg">
          Error:
          {error.type || ''}
        </div>
      )}
    </div>
  );
}

FormSelect.defaultProps = { formOptionsEmbedded: false, ...{} };

export default FormSelect;
